import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, RotateCcw, Loader2, Image as ImageIcon, Film } from "lucide-react";
import { adminApi, apiError } from "@/lib/adminApi";
import { imgUrl, Spinner } from "@/components/admin/shared";
import { PDP_MEDIA } from "@/data/pdpMedia";

const GROUPS = [...new Set(PDP_MEDIA.map((m) => m.group))];

export function MediaPanel() {
  const [media, setMedia] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const refs = useRef({});

  const load = async () => {
    const m = await adminApi.get("/admin/media");
    setMedia(Object.fromEntries((m.data || []).map((d) => [d.slot, d])));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (slot, file) => {
    if (!file) return;
    setUploadingSlot(slot);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await adminApi.post(`/admin/media/${slot}`, fd);
      toast.success("Media updated");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingSlot(null);
    }
  };

  const onReset = async (slot) => {
    try {
      await adminApi.delete(`/admin/media/${slot}`);
      toast.success("Reverted to default");
      await load();
    } catch {
      toast.error("Reset failed");
    }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-media-manager">
      <h1 className="font-display text-3xl font-light text-[#2C2B2C]">Media Manager</h1>
      <p className="mt-2 max-w-2xl text-sm text-[#8A7F86]">Swap any photo or video across your storefront — the home page and the product page. Images up to 8MB (jpg, png, webp, gif); videos up to 64MB (mp4, webm, mov). Changes go live instantly.</p>
      <div className="mt-8 space-y-10">
        {GROUPS.map((group) => (
          <div key={group}>
            <h2 className="mb-4 font-display text-2xl font-light text-[#2C2B2C]">{group}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {PDP_MEDIA.filter((m) => m.group === group).map((m) => {
                const doc = media[m.key];
                const overridden = !!doc;
                const src = imgUrl(doc?.url) || m.def;
                const isVideo = m.type === "video";
                return (
                  <div key={m.key} className="overflow-hidden rounded-3xl border border-[#E5DBC8] bg-white" data-testid={`media-card-${m.key}`}>
                    <div className="aspect-video overflow-hidden bg-[#F5EFE3]">
                      {isVideo ? <video src={src} muted loop playsInline className="h-full w-full object-cover" key={src} /> : <img src={src} alt={m.label} className="h-full w-full object-cover" />}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2">{isVideo ? <Film className="h-4 w-4 text-[#FBC4A8]" /> : <ImageIcon className="h-4 w-4 text-[#FBC4A8]" />}<p className="text-sm font-medium text-[#2C2B2C]">{m.label}</p></div>
                      <p className="mt-1 text-xs text-[#8A7F86]">{isVideo ? "Video" : "Image"}{overridden && " · custom"}</p>
                      <input ref={(el) => (refs.current[m.key] = el)} type="file" accept={isVideo ? "video/mp4,video/webm,video/quicktime" : "image/png,image/jpeg,image/webp,image/gif"}
                        className="hidden" data-testid={`media-input-${m.key}`} onChange={(e) => onUpload(m.key, e.target.files?.[0])} />
                      <div className="mt-4 flex gap-2">
                        <button onClick={() => refs.current[m.key]?.click()} disabled={uploadingSlot === m.key} data-testid={`media-upload-${m.key}`}
                          className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#232222] py-2.5 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] hover:bg-[#4A2A38] disabled:opacity-60">
                          {uploadingSlot === m.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Replace
                        </button>
                        {overridden && (
                          <button onClick={() => onReset(m.key)} data-testid={`media-reset-${m.key}`}
                            className="flex items-center justify-center rounded-full border border-[#DACDB4] px-3 text-[#8A7F86] hover:border-[#FBC4A8]"><RotateCcw className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
